import { useState, useRef, useEffect } from "react";
import { Transcript } from "../api/models/Transcript";
import { EarningsDate } from "../api/models/EarningsDate";

export const TranscriptComponent = ({supportedTickers, setCurrentTicker, ticker, transcript, currentFiscalDateEnding, setCurrentFiscalDateEnding, earningsDates }:
    {
        supportedTickers: string[];
        setCurrentTicker: (ticker: string) => void;
        ticker: string;
        transcript: Transcript | undefined;
        setCurrentFiscalDateEnding: (date: string) => void;
        currentFiscalDateEnding: string
        earningsDates: EarningsDate[]
    }) => {
    const [tooltip, setTooltip] = useState({ visible: false, title: '', position: { x: 0, y: 0 } });
    const [tickerSearch, setTickerSearch] = useState('');
    const [isTickerDropdownOpen, setIsTickerDropdownOpen] = useState(false);
    const tickerDropdownRef = useRef<HTMLDivElement>(null);

    // Filter tickers based on search
    const filteredTickers = supportedTickers.filter(ticker =>
        ticker.toLowerCase().includes(tickerSearch.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tickerDropdownRef.current && !tickerDropdownRef.current.contains(event.target as Node)) {
                setIsTickerDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const showTooltip = (title: string, event: React.MouseEvent) => {
        setTooltip({
            visible: true,
            title: title,
            position: { x: event.clientX, y: event.clientY }
        });
    };

    const hideTooltip = () => {
        setTooltip({ visible: false, title: '', position: { x: 0, y: 0 } });
    };

    return (
        <div className="flex flex-col h-full rounded-lg flex-1">
            {/* Sticky Header */}
            <div className="flex justify-between items-center sticky top-0 z-10 p-4">
                <h1 className="font-bold hidden xl:block">Transcript</h1>
                <div className="relative ml-2 w-80 flex items-center">
                    {/* Searchable Ticker Dropdown */}
                    <div className="relative w-1/2" ref={tickerDropdownRef}>
                        <input
                            type="text"
                            className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 cursor-pointer"
                            value={isTickerDropdownOpen ? tickerSearch : (ticker || '')}
                            onChange={(e) => {
                                setTickerSearch(e.target.value);
                                setIsTickerDropdownOpen(true);
                            }}
                            onFocus={() => {
                                setTickerSearch('');
                                setIsTickerDropdownOpen(true);
                            }}
                            placeholder="Search ticker..."
                            readOnly={!isTickerDropdownOpen}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                        
                        {isTickerDropdownOpen && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {filteredTickers.length > 0 ? (
                                    filteredTickers.map((ticker) => (
                                        <div
                                            key={ticker}
                                            className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                                            onClick={() => {
                                                setCurrentTicker(ticker);
                                                setIsTickerDropdownOpen(false);
                                                setTickerSearch('');
                                            }}
                                        >
                                            {ticker}
                                        </div>
                                    ))
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                        No tickers found
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <select
                        className="w-full ml-2 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 appearance-none"
                        value={currentFiscalDateEnding}
                        onChange={(e) => {
                            setCurrentFiscalDateEnding(e.target.value);
                        }}
                    >
                        {earningsDates.map((item) => (
                            <option
                                disabled={!item.userHasAccess}
                                key={`${item.date}-${item.fiscalPeriod}-${item.fiscalYear}`}
                                value={item.date}
                            >
                                {item.date}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Scrollable Transcript Section */}
            {transcript === undefined && (
                <div className="flex-1 overflow-auto pl-4">
                    Transcript not available for {currentFiscalDateEnding || ticker}
                </div>
            )}
            {transcript !== undefined && (
                <div
                    className="flex-1 flex flex-col items-start space-y-2 scroll-smooth overflow-auto p-4"
                >
                    {transcript?.paragraphs?.map((p, i) => {
                        const participant = transcript?.participants?.find(
                            (x) => x.name === p.speaker
                        );
                        return (
                            <div
                                key={`${p.id}`}
                                className={`${!p.title?.includes("Analyst")
                                    ? "bg-gray-200 rounded-lg py-2 px-4 max-w-xs"
                                    : "bg-blue-500 rounded-lg py-2 px-4 max-w-xs text-white self-end"
                                    }`}
                            >
                                <p
                                    onMouseOver={(e) => {
                                        if (participant?.name === "Operator") {
                                            return;
                                        }
                                        showTooltip(participant?.title || "No title available", e);
                                    }}
                                    onMouseOut={hideTooltip}
                                    className="font-bold cursor-pointer underline"
                                >
                                    {p.speaker}:
                                </p>
                                <br />
                                <p>{p.text}</p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Tooltip */}
            {tooltip.visible && (
                <div
                    className="absolute bg-gray-700 text-white p-2 rounded shadow-md text-sm"
                    style={{
                        top: tooltip.position.y - 75,
                        left: tooltip.position.x - 10,
                    }}
                >
                    {tooltip.title}
                </div>
            )}
        </div>
    );
};